/**
 * MONSTER TOWN — NEIGHBOUR PRESENTS. NPC CREW's content drop (data only).
 *
 * The daily-return hook made physical: a neighbour leaves a present on the
 * doorstep while the kid is away, and it is sitting there when they come back.
 * Tapping it opens it, coins fly out, and the monster cheers.
 *
 * FREE FOREVER on purpose — it is also a lovely thing for a kid to place around
 * their own town, and nothing in this game should ever be un-placeable.
 */

import { FOOT_1x1 } from '../core/grid'
import { definePlaceable } from '../core/placeable'
import type { PlaceableId } from '../core/types'
import { ball, box, boxOn, group, inked } from '../render/kit'

/** Coins inside a present. Data — the economy crew tunes this, not code. */
const GIFT_COINS_BASE = 22
const GIFT_COINS_BONUS = 16

export const giftBox = definePlaceable<{ opened: boolean; peeks: number }>({
  id: 'prop_gift' as PlaceableId,
  devName: 'Wrapped present',
  category: 'townProp',
  scale: 'both',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: true,
  walkable: false,
  tints: ['flowerPink', 'roofTeal', 'flowerYellow', 'wallLilac', 'roofRed'],
  tags: ['gift', 'cosy', 'daily'],
  initialState: () => ({ opened: false, peeks: 0 }),

  buildArt: (ctx) => {
    const t = ctx.grid.tileSize
    const g = group('prop_gift')
    const paper = ctx.tint ?? 'flowerPink'
    const opened = ctx.instance?.state?.['opened'] === true

    const body = boxOn(t * 0.58, t * 0.44, t * 0.58, { color: paper, name: 'body' })
    g.add(inked(body))

    // Ribbon: two straps crossing the box.
    g.add(boxOn(t * 0.12, t * 0.46, t * 0.6, { color: 'flowerRed', name: 'ribbonA' }))
    g.add(boxOn(t * 0.6, t * 0.46, t * 0.12, { color: 'flowerRed', name: 'ribbonB' }))

    // The lid is its own node so it can be lifted off when the present opens.
    const lid = group('lid')
    lid.position.y = t * 0.44
    lid.add(boxOn(t * 0.66, t * 0.12, t * 0.66, { color: paper, name: 'lidTop' }))
    const bowL = ball(t * 0.11, { color: 'flowerRed', x: -t * 0.09, y: t * 0.18 })
    const bowR = ball(t * 0.11, { color: 'flowerRed', x: t * 0.09, y: t * 0.18 })
    lid.add(bowL, bowR)
    if (opened) {
      lid.position.set(t * 0.34, t * 0.5, t * 0.2)
      lid.rotation.z = -0.7
      // Something sparkly is clearly still inside.
      const treasure = ball(t * 0.16, { color: 'uiCoin', y: t * 0.5, name: 'treasure' })
      g.add(treasure)
      g.add(box(t * 0.06, t * 0.2, t * 0.06, { color: 'flowerYellow', y: t * 0.66, name: 'spark' }))
    }
    g.add(lid)
    return g
  },

  // LAW #1: the first tap is the payoff — the lid pops, confetti, coins, and
  // the monster cheers. After that it is an open present a kid can still play
  // with: butterflies come out and it giggles. Nothing is ever "used up".
  interact: (ctx) => {
    if (ctx.state.opened !== true) {
      const coins = GIFT_COINS_BASE + Math.floor(ctx.rng() * GIFT_COINS_BONUS)
      return {
        feedback: [
          { kind: 'toggle', on: true, target: 'lid' },
          { kind: 'particles', effect: 'confetti', count: 16 },
          { kind: 'coins', amount: coins, source: 'daily.gift' },
          { kind: 'monster', action: 'cheer', seconds: 1.8 },
          { kind: 'sound', cue: 'gift_open' },
          { kind: 'state', patch: { opened: true } },
        ],
        cooldownMs: 400,
      }
    }
    const peeks = ctx.state.peeks + 1
    return {
      feedback: [
        { kind: 'particles', effect: peeks % 3 === 0 ? 'butterfly' : 'sparkle', count: 6 },
        { kind: 'monster', action: peeks % 3 === 0 ? 'laugh' : 'hop', seconds: 1.2 },
        { kind: 'sound', cue: 'pop_soft' },
        { kind: 'state', patch: { peeks } },
      ],
      cooldownMs: 250,
    }
  },
})

export const NPC_CONTENT = [giftBox]
/** A present is never something a kid has to buy before they can place one. */
export const NPC_FREE_IDS: PlaceableId[] = [giftBox.id]
